/**
 * Вкладка Aelo в карточке CRM Bitrix24 (#745).
 *
 * Bitrix24 открывает вкладку тем же входом `/api/v1/embedded/app`, что и
 * плитку приложения, и называет карточку двумя полями формы: `PLACEMENT`
 * (код места, например `CRM_DEAL_DETAIL_TAB`) и `PLACEMENT_OPTIONS` (JSON с
 * `ID` сущности). Таблица ниже — единственное место, где код места
 * связан с типом сущности: вход читает её в одну сторону, восстановление
 * сессии в SPA — в другую.
 *
 * Спека: docs/plans/2026-10-17-bitrix24-deal-tab.md §4.2.
 */

import { z } from 'zod/v4';
import { CRM_ENTITY_ID_PATTERN } from './records';

/** Сущности, в карточке которых живёт вкладка. Смарт-процессы — вне объёма (§5). */
export const crmTabEntityTypeSchema = z.enum(['deal', 'lead', 'contact', 'company']);
export type CrmTabEntityType = z.infer<typeof crmTabEntityTypeSchema>;

/**
 * Номер сущности в адресе экрана вкладки — тот же формат, что у фильтра
 * записей `crmEntityId` (строка цифр, ≤ 20): экран передаёт его туда как есть.
 * Ведущего нуля у номера Bitrix24 не бывает: `0` — не сущность, а `0123`
 * никогда не совпадёт с хранимым `123`. Вход, адрес экрана и восстановление
 * сессии читают номер этой одной схемой (`parseCrmTabEntityId`).
 */
export const crmTabEntityIdSchema = z
  .string()
  .regex(CRM_ENTITY_ID_PATTERN)
  .refine((id) => !id.startsWith('0'));

/**
 * Номер сущности из любого источника: числом (так его отдают `JSON.parse`
 * формы Bitrix24 и разбор адреса SPA) или строкой. Число — только безопасное
 * целое: за 2^53 цифры уже потеряны. Всё, что не номер, — `null`.
 */
export function parseCrmTabEntityId(raw: unknown): string | null {
  const candidate =
    typeof raw === 'number' ? (Number.isSafeInteger(raw) ? String(raw) : null) : raw;
  const parsed = crmTabEntityIdSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

/** Тип сущности → код placement вкладки в её карточке. */
export const crmTabPlacementByEntity: Readonly<Record<CrmTabEntityType, string>> =
  Object.freeze({
    deal: 'CRM_DEAL_DETAIL_TAB',
    lead: 'CRM_LEAD_DETAIL_TAB',
    contact: 'CRM_CONTACT_DETAIL_TAB',
    company: 'CRM_COMPANY_DETAIL_TAB',
  });

/**
 * Код placement → тип сущности. Обратная таблица собрана из прямой, чтобы
 * две стороны не разошлись. Читать с проверкой собственного ключа
 * (`Object.prototype.hasOwnProperty.call`): `in` и индексация видят
 * `toString` из прототипа, а ключ приходит из формы Bitrix24.
 */
export const crmTabEntityByPlacement: Readonly<Record<string, CrmTabEntityType>> =
  Object.freeze(
    Object.fromEntries(
      (Object.entries(crmTabPlacementByEntity) as Array<[CrmTabEntityType, string]>).map(
        ([entityType, placement]) => [placement, entityType]
      )
    )
  );

/**
 * Разовый проход владельца платформы: привязать вкладку к уже установленным
 * порталам (`POST /api/v1/admin/bitrix24/crm-tabs`, #745). Список установок
 * обязателен и ограничен — «все установки» тела не бывает: проход пишет в
 * чужие порталы, и каждый должен быть назван.
 */
export const CRM_TAB_PASS_MAX_INSTALLATIONS = 50;

export const crmTabPassModeSchema = z.enum(['dry_run', 'bind', 'unbind']);
export type CrmTabPassMode = z.infer<typeof crmTabPassModeSchema>;

export const crmTabPassRequestSchema = z.object({
  mode: crmTabPassModeSchema.default('dry_run'),
  installationIds: z
    .array(z.string().min(1).max(128))
    .min(1)
    .max(CRM_TAB_PASS_MAX_INSTALLATIONS),
});
export type CrmTabPassRequest = z.infer<typeof crmTabPassRequestSchema>;

/**
 * Исход по установке: исход по каждому placement либо причина, по которой
 * портал не тронут. `not_connected` и `unavailable` — отказы токен-менеджера
 * (портал отверг обновление токена / не ответил), которым не подходит ни одно
 * из имён «битые учётные данные» и «выключена». `not_attempted` — проход
 * исчерпал бюджет запроса до этой установки и её не трогал: повторить вызов
 * с этими id.
 */
export const crmTabPassOutcomeSchema = z.union([
  z.record(z.string(), z.string()),
  z.enum([
    'credentials_unreadable',
    'not_bitrix24',
    'inactive',
    'not_found',
    'not_connected',
    'unavailable',
    'failed',
    'not_attempted',
  ]),
]);

export const crmTabPassResponseSchema = z.object({
  results: z.array(
    z.object({
      installationId: z.string(),
      outcome: crmTabPassOutcomeSchema,
    })
  ),
});
export type CrmTabPassResponse = z.infer<typeof crmTabPassResponseSchema>;
